import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Add, Cancel, Edit, HelpOutline, PersonAdd } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import GameIconSelect from '../../../features/game/components/GameIconSelect/GameIconSelect'
import { Analyst } from '../../../types/Analyst'
import { useGetIntercomAdminsQuery, useGetInternalAnalystsQuery, useSaveAnalystMutation } from '../../api/analysts'
import { useGetInternalConventionalItemsQuery } from '../../api/conventional-genres'
import { useGetGameAnalystMapQuery, useGetInternalGameDocumentQuery, useSaveGameAnalystMutation, useSaveInternalGameMutation } from '../../api/games'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { useGetInternalStoresQuery } from '../../api/stores'
import { AnalystEditDialog } from '../../features/analysts/components/AnalystEditDialog/AnalystEditDialog'
import { analystFieldLabelKeys, getUnstoredAnalystFields } from '../../features/analysts/helpers/analystHelpers'
import { AnalystMutation } from '../../features/analysts/types/AnalystMutation'
import { ConventionalItemType } from '../../features/conventional-genres/types/InternalConventionalItem'
import {
  addPositionId,
  gameFieldLabelKeys,
  gameStages,
  genreInnovatorOptions,
  getDialogPositions,
  getSoftLaunchMarketOptions,
  getSubgenreGroups,
  getUnstoredGameFields,
  softLaunchStageId,
  toEditGameFormValues,
  toGameMutation,
  visiblePlatforms,
} from '../../features/games/helpers/editGameHelpers'
import { EditGameFormValues, InternalGameDocument } from '../../features/games/types/InternalGame'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { InternalFormDialog } from '../InternalFormDialog/InternalFormDialog'

const newGameKey = 'new'
const discardAction = 'discard'
const isAppIdValid = (appId: string) => /^\d*$/.test(appId.trim())

type EditGameDialogProps = {
  open: boolean
  // None for a new game.
  gameId?: string
  onClose: () => void
  // The game as stored after a save, also when some of its fields weren't stored and the dialog stays open.
  onSaved?: (game: InternalGameDocument) => void
}

type AnalystDialogState = { open: boolean; analyst?: Analyst }

export const EditGameDialog: FC<EditGameDialogProps> = ({ open, gameId, onClose, onSaved }) => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const [createdId, setCreatedId] = useState<string>()
  const currentId = gameId ?? createdId
  const formKey = currentId ?? newGameKey
  const [loadedKey, setLoadedKey] = useState<string>()
  const [baseDocument, setBaseDocument] = useState<InternalGameDocument>()
  const [edited, setEdited] = useState(false)
  const [isConfirmingClose, setIsConfirmingClose] = useState(false)
  const [pendingPositionId, setPendingPositionId] = useState('')
  const [analystDialog, setAnalystDialog] = useState<AnalystDialogState>({ open: false })

  const documentQuery = useGetInternalGameDocumentQuery(currentId ?? '', { skip: !open || !currentId, refetchOnMountOrArgChange: true })
  const analystMapQuery = useGetGameAnalystMapQuery(undefined, { skip: !open, refetchOnMountOrArgChange: true })
  const storesQuery = useGetInternalStoresQuery(undefined, { skip: !open })
  const marketsQuery = useGetInternalMarketsQuery(undefined, { skip: !open })
  const subgenresQuery = useGetInternalConventionalItemsQuery(ConventionalItemType.Subgenre, { skip: !open })
  const analystsQuery = useGetInternalAnalystsQuery(undefined, { skip: !open })
  const intercomAdminsQuery = useGetIntercomAdminsQuery(undefined, { skip: !analystDialog.open })
  const [saveGame, { isLoading: isSavingGame }] = useSaveInternalGameMutation()
  const [saveGameAnalyst, { isLoading: isSavingGameAnalyst }] = useSaveGameAnalystMutation()
  const [saveAnalyst, { isLoading: isSavingAnalyst }] = useSaveAnalystMutation()
  const isSaving = isSavingGame || isSavingGameAnalyst

  const { control, handleSubmit, reset, setValue, watch } = useForm<EditGameFormValues>({ defaultValues: toEditGameFormValues(undefined, {}) })
  const [name, desc, appId, analystId, platforms, positionIds, conventionalSubgenre, softLaunchMarket] = useWatch({
    control,
    name: ['name', 'desc', 'appId', 'analystId', 'platforms', 'positionIds', 'conventionalSubgenre', 'softLaunchMarket'],
  })

  // Sticky, as legacy's form: changing a field and back still allows saving the game unchanged.
  useEffect(() => {
    const subscription = watch((_values, { name: field }) => field && setEdited(true))
    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    if (open) return
    setCreatedId(undefined)
    setLoadedKey(undefined)
    setBaseDocument(undefined)
    setIsConfirmingClose(false)
    setAnalystDialog({ open: false })
  }, [open])

  const isDocumentReady = !currentId || (!!documentQuery.data && !documentQuery.isFetching)
  const isReady = open && isDocumentReady && !!analystMapQuery.data && !analystMapQuery.isFetching && !!storesQuery.data && !!marketsQuery.data
  const isLoadError = documentQuery.isError || analystMapQuery.isError || storesQuery.isError || marketsQuery.isError

  // Once per opened game, so a refetch after a save can't replace edits that weren't stored.
  useEffect(() => {
    if (!isReady || isLoadError || loadedKey === formKey) return
    const document = currentId ? documentQuery.data : undefined
    reset(toEditGameFormValues(document, analystMapQuery.data ?? {}))
    setBaseDocument(document)
    setEdited(false)
    setLoadedKey(formKey)
  }, [isReady, isLoadError, loadedKey, formKey, currentId, documentQuery.data, analystMapQuery.data, reset])

  const isFormLoaded = loadedKey === formKey
  const positions = useMemo(
    () => getDialogPositions(storesQuery.data ?? [], marketsQuery.data ?? [], baseDocument),
    [storesQuery.data, marketsQuery.data, baseDocument]
  )
  const positionsById = useMemo(() => new Map(positions.map((position) => [position.id, position])), [positions])
  const availablePositions = useMemo(() => positions.filter(({ id }) => !positionIds.includes(id)), [positions, positionIds])
  const selectedPendingPositionId = availablePositions.some(({ id }) => id === pendingPositionId) ? pendingPositionId : availablePositions[0]?.id ?? ''
  const subgenreGroups = useMemo(
    () => getSubgenreGroups(subgenresQuery.data ?? [], conventionalSubgenre, t('internal-analysts:games_subgenre_not_listed')),
    [subgenresQuery.data, conventionalSubgenre, t]
  )
  const softLaunchMarketOptions = useMemo(() => getSoftLaunchMarketOptions(positions, softLaunchMarket), [positions, softLaunchMarket])
  const analysts = useMemo(() => [...(analystsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [analystsQuery.data])
  const selectedAnalyst = analysts.find(({ id }) => id === analystId)

  const hasAppId = appId.trim() !== ''
  const appIdValid = isAppIdValid(appId)
  const canSave = isFormLoaded && !isLoadError && !isSaving && edited && !!name.trim() && !!desc.trim() && appIdValid && positionIds.length > 0

  const requestClose = () => {
    if (isSaving) return
    if (edited) setIsConfirmingClose(true)
    else onClose()
  }

  const handleSave = handleSubmit(async (values) => {
    const body = toGameMutation(baseDocument, values, positions)
    const saved = await run(saveGame(body).unwrap(), {
      error: t('internal-analysts:games_save_failed'),
      errorWithReason: (reason) => t('internal-analysts:games_save_failed_reason', { reason }),
    })
    if (!saved.ok) return
    const stored = saved.result
    setBaseDocument(stored)
    if (!baseDocument && stored.id) {
      setCreatedId(stored.id)
      setLoadedKey(stored.id)
    }
    onSaved?.(stored)

    const unstored = Array.from(new Set(getUnstoredGameFields(body, stored).map((field) => t(gameFieldLabelKeys[field]))))
    let analystFailed = false
    const storedAppId = stored.appId === null || stored.appId === undefined ? '' : String(stored.appId)
    if (storedAppId && values.analystId !== (analystMapQuery.data?.[storedAppId] ?? '')) {
      try {
        const storedMap = await saveGameAnalyst({ appId: storedAppId, analystId: values.analystId }).unwrap()
        if ((storedMap[storedAppId] ?? '') !== values.analystId) unstored.push(t('internal-analysts:games_analyst'))
      } catch {
        analystFailed = true
      }
    }

    if (unstored.length || analystFailed) {
      const messages = [
        unstored.length ? t('internal-analysts:games_not_stored', { fields: unstored.join(', ') }) : '',
        analystFailed ? t('internal-analysts:games_analyst_save_failed') : '',
      ]
      notifyError(messages.filter(Boolean).join(' '))
      return
    }
    notifySuccess(baseDocument ? t('internal-analysts:games_saved', { name: stored.name }) : t('internal-analysts:games_added', { name: stored.name }))
    onClose()
  })

  const handleSaveAnalyst = async (analyst: AnalystMutation) => {
    const saved = await run(saveAnalyst(analyst).unwrap(), { error: t('internal-analysts:analysts_save_failed') })
    if (!saved.ok) return
    const stored = saved.result
    const unstored = getUnstoredAnalystFields(analyst, stored)
    if (unstored.length) {
      notifyError(t('internal-analysts:analysts_not_stored', { fields: unstored.map((field) => t(analystFieldLabelKeys[field])).join(', ') }))
      return
    }
    notifySuccess(t('internal-analysts:analysts_saved', { name: stored.name }))
    setValue('analystId', stored.id, { shouldDirty: true })
    setAnalystDialog({ open: false })
  }

  const subgenreItems: ReactNode[] = [
    <MenuItem key="none" value="">
      {t('internal-analysts:games_subgenre_none')}
    </MenuItem>,
    ...subgenreGroups.flatMap(({ genre, names }) => [
      <ListSubheader key={`genre-${genre}`}>{genre}</ListSubheader>,
      ...names.map((subgenre) => (
        <MenuItem key={`${genre}-${subgenre}`} value={subgenre}>
          {subgenre}
        </MenuItem>
      )),
    ]),
  ]

  const renderForm = () => (
    <Stack spacing={2} pt={1}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t('internal-analysts:games_name')}
                  required
                  error={!field.value.trim()}
                  helperText={!field.value.trim() ? t('internal-analysts:games_name_required') : undefined}
                />
              )}
            />
            <Controller
              name="desc"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t('internal-analysts:games_description')}
                  required
                  multiline
                  minRows={3}
                  error={!field.value.trim()}
                  helperText={!field.value.trim() ? t('internal-analysts:games_description_required') : undefined}
                />
              )}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormLabel>{t('internal-analysts:games_icon')}</FormLabel>
          <Controller
            name="icon"
            control={control}
            render={({ field }) => (
              <GameIconSelect selectedIcon={field.value} onFileSelected={(file) => field.onChange(file)} onFileRemoved={() => field.onChange(null)} />
            )}
          />
        </Grid>
      </Grid>
      <Divider />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <Controller
              name="artist"
              control={control}
              render={({ field }) => <TextField {...field} size="small" label={t('internal-analysts:games_artist')} />}
            />
            <Controller
              name="appId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t('internal-analysts:games_app_id')}
                  inputProps={{ inputMode: 'numeric' }}
                  error={!appIdValid}
                  helperText={appIdValid ? t('internal-analysts:games_app_id_help') : t('internal-analysts:games_app_id_invalid')}
                />
              )}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Controller
              name="analystId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size="small"
                  label={t('internal-analysts:games_analyst')}
                  disabled={!hasAppId}
                  error={analystsQuery.isError}
                  helperText={
                    analystsQuery.isError
                      ? t('internal-analysts:analysts_load_failed')
                      : hasAppId
                      ? undefined
                      : t('internal-analysts:games_analyst_needs_app_id')
                  }
                >
                  <MenuItem value="">{t('internal-analysts:games_analyst_none')}</MenuItem>
                  {analysts.map((analyst) => (
                    <MenuItem key={analyst.id} value={analyst.id}>
                      {analyst.name}
                    </MenuItem>
                  ))}
                  {field.value && !selectedAnalyst && <MenuItem value={field.value}>{field.value}</MenuItem>}
                </TextField>
              )}
            />
            <Tooltip title={t('internal-analysts:games_analyst_edit')}>
              <span>
                <IconButton
                  aria-label={t('internal-analysts:games_analyst_edit')}
                  disabled={!selectedAnalyst}
                  onClick={() => setAnalystDialog({ open: true, analyst: selectedAnalyst })}
                >
                  <Edit />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('internal-analysts:games_analyst_create')}>
              <IconButton aria-label={t('internal-analysts:games_analyst_create')} onClick={() => setAnalystDialog({ open: true })}>
                <PersonAdd />
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Controller
            name="conventionalSubgenre"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                label={t('internal-analysts:games_subgenre')}
                error={subgenresQuery.isError}
                helperText={subgenresQuery.isError ? t('internal-analysts:games_subgenres_load_failed') : undefined}
              >
                {subgenreItems}
              </TextField>
            )}
          />
          <Controller
            name="overrideConventionals"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                label={t('internal-analysts:games_override_subgenre')}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="genreInnovator"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                label={t('internal-analysts:games_importance')}
                onChange={(event) => field.onChange(event.target.value === '' ? '' : Number(event.target.value))}
              >
                <MenuItem value="">{t('internal-analysts:games_importance_unset')}</MenuItem>
                {genreInnovatorOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
      </Grid>
      <Box>
        <FormLabel>{t('internal-analysts:games_platforms')}</FormLabel>
        <FormGroup row>
          {visiblePlatforms.map((platform) => (
            <FormControlLabel
              key={platform.id}
              label={platform.label}
              control={
                <Checkbox
                  checked={platforms.includes(platform.id)}
                  onChange={(event) =>
                    setValue('platforms', event.target.checked ? [...platforms, platform.id] : platforms.filter((id) => id !== platform.id), {
                      shouldDirty: true,
                    })
                  }
                />
              }
            />
          ))}
        </FormGroup>
      </Box>
      <Controller
        name="archive"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
            label={t('internal-analysts:games_archive')}
          />
        )}
      />
      <Box>
        <FormLabel id="edit-game-stage">{t('internal-analysts:games_stage')}</FormLabel>
        <Controller
          name="stageId"
          control={control}
          render={({ field }) => (
            <RadioGroup row aria-labelledby="edit-game-stage" value={String(field.value)} onChange={(event) => field.onChange(Number(event.target.value))}>
              {gameStages.map((stage) => (
                <FormControlLabel
                  key={stage.id}
                  value={String(stage.id)}
                  control={<Radio />}
                  label={
                    stage.id === softLaunchStageId ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{t(stage.labelKey)}</span>
                        <Tooltip title={t('internal-analysts:games_stage_soft_launch_help')}>
                          <HelpOutline fontSize="small" color="action" />
                        </Tooltip>
                      </Stack>
                    ) : (
                      t(stage.labelKey)
                    )
                  }
                />
              ))}
            </RadioGroup>
          )}
        />
      </Box>
      <Divider />
      <Stack direction="row" spacing={1} alignItems="center">
        <Controller
          name="softLaunchMarket"
          control={control}
          render={({ field }) => (
            <TextField {...field} select fullWidth size="small" label={t('internal-analysts:games_soft_launch_market')}>
              <MenuItem value="">{t('internal-analysts:games_soft_launch_market_none')}</MenuItem>
              {softLaunchMarketOptions.map((option) => (
                <MenuItem key={option.iso} value={option.iso}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Button sx={{ flexShrink: 0 }} disabled={!softLaunchMarket} onClick={() => setValue('softLaunchMarket', '', { shouldDirty: true })}>
          {t('internal-analysts:games_soft_launch_market_clear')}
        </Button>
      </Stack>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            select
            fullWidth
            size="small"
            label={t('internal-analysts:games_positions')}
            value={selectedPendingPositionId}
            disabled={!availablePositions.length}
            onChange={(event) => setPendingPositionId(event.target.value)}
          >
            {availablePositions.map((position) => (
              <MenuItem key={position.id} value={position.id}>
                {position.fullName}
              </MenuItem>
            ))}
          </TextField>
          <Button
            sx={{ flexShrink: 0 }}
            startIcon={<Add />}
            disabled={!selectedPendingPositionId}
            onClick={() => setValue('positionIds', addPositionId(positionIds, selectedPendingPositionId, positions), { shouldDirty: true })}
          >
            {t('internal-analysts:games_position_add')}
          </Button>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
          {positionIds.length ? (
            positionIds.map((id) => {
              const label = positionsById.get(id)?.fullName ?? id
              return (
                <Chip
                  key={id}
                  label={label}
                  onDelete={() =>
                    setValue(
                      'positionIds',
                      positionIds.filter((positionId) => positionId !== id),
                      { shouldDirty: true }
                    )
                  }
                  deleteIcon={<Cancel titleAccess={t('internal-analysts:games_position_remove', { name: label })} />}
                />
              )
            })
          ) : (
            <Typography variant="body2" color="error.main">
              {t('internal-analysts:games_positions_none')}
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  )

  return (
    <>
      <InternalFormDialog
        open={open}
        title={
          currentId
            ? t('internal-analysts:games_edit_title', { name: baseDocument?.name ?? documentQuery.data?.name ?? '' })
            : t('internal-analysts:games_add_title')
        }
        onClose={requestClose}
        onSave={handleSave}
        saveDisabled={!canSave}
        isSaving={isSaving}
        maxWidth="md"
      >
        {isLoadError ? (
          <Typography color="error.main">{t('internal-analysts:games_load_one_failed')}</Typography>
        ) : isFormLoaded ? (
          renderForm()
        ) : (
          <Skeleton variant="rectangular" height={480} />
        )}
      </InternalFormDialog>

      <AnalystEditDialog
        open={analystDialog.open}
        analyst={analystDialog.analyst}
        intercomAdmins={intercomAdminsQuery.data}
        isLoadingIntercomAdmins={intercomAdminsQuery.isLoading}
        isSaving={isSavingAnalyst}
        onClose={() => setAnalystDialog({ open: false })}
        onSave={handleSaveAnalyst}
      />

      <ConfirmDialog
        open={isConfirmingClose}
        confirmDialogData={{
          title: t('internal-analysts:games_discard_title'),
          confirmText: t('internal-analysts:games_discard_confirm'),
          actionText: t('internal-analysts:games_discard'),
          destructiveAction: true,
          data: discardAction,
        }}
        onClose={(confirmed) => {
          setIsConfirmingClose(false)
          if (confirmed?.data === discardAction) onClose()
        }}
      />
    </>
  )
}
